import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSSEConnected } from '../../hooks/useSSE'
import { usePersonaStatusMap } from '../../hooks/usePersonaStatusMap'
import { fetchPersonaState, spawnPersona } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import PersonaMetricsGrid from '../../components/PersonaMetricsGrid'
import type { PersonaState } from '../../lib/types'

interface PersonaFull extends PersonaState {
  name: string
  queue_count?: number
  category?: string
}

function ContextBar({ pct }: { pct: number }) {
  const color = pct >= 75 ? '#ef4444' : pct >= 50 ? '#d29922' : '#856FC8'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 48, height: 4, borderRadius: 2, overflow: 'hidden', background: 'var(--bg-elevated)' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 2, background: color }} />
      </div>
      <span style={{ fontSize: 10, fontFamily: 'monospace', color }}>{pct}%</span>
    </div>
  )
}

export default function StatusDashboardPage() {
  const [spawning, setSpawning] = useState<Record<string, boolean>>({})
  const [tab, setTab] = useState<'status' | 'metrics'>('status')
  const sseOk = useSSEConnected()

  const { data: state } = useQuery({
    queryKey: ['personaState'],
    queryFn: fetchPersonaState,
    refetchInterval: sseOk ? false : 15_000,
  })

  const statusMap = usePersonaStatusMap()

  const personas = state
    ? Object.entries(state).map(([name, s]) => ({ ...s, name } as PersonaFull)).sort((a, b) => {
        if (a.awake && !b.awake) return -1
        if (!a.awake && b.awake) return 1
        return a.name.localeCompare(b.name)
      })
    : []

  const awake = personas.filter(p => p.awake)
  const sleeping = personas.filter(p => !p.awake)

  const handleWake = useCallback(async (name: string) => {
    if (spawning[name]) return
    setSpawning(prev => ({ ...prev, [name]: true }))
    try { await spawnPersona(name) } catch {}
    setTimeout(() => setSpawning(prev => ({ ...prev, [name]: false })), 8000)
  }, [spawning])

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <h1 style={{
            fontFamily: 'Playfair Display, Georgia, serif',
            fontSize: 24, fontWeight: 700, marginBottom: 4,
          }}>
            Operations
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {awake.length} persona{awake.length !== 1 ? 's' : ''} awake of {personas.length} total
          </p>
        </div>

        {/* Tab toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {(['status', 'metrics'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '6px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: tab === t ? 'var(--bg-elevated)' : 'transparent',
                color: tab === t ? 'var(--text-primary)' : 'var(--text-muted)',
                border: `1px solid ${tab === t ? 'var(--border-strong, #3a3a44)' : 'var(--border)'}`,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {t === 'status' ? 'Persona Status' : 'Growth Metrics'}
            </button>
          ))}
        </div>

        {/* Metrics View */}
        {tab === 'metrics' && (
          <PersonaMetricsGrid />
        )}

        {/* Awake */}
        {tab === 'status' && (<>
        {awake.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 11, textTransform: 'uppercase', letterSpacing: '1.2px',
              color: 'var(--text-muted)', marginBottom: 12, paddingLeft: 4,
            }}>
              Awake
            </div>
            <div style={{
              display: 'grid', gap: 10,
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            }}>
              {awake.map(p => {
                const entry = statusMap[p.name.toLowerCase()]
                const level = entry?.level ?? 'active'
                const queueCount = entry?.queueCount ?? 0
                return (
                  <div key={p.name} style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 12, padding: 16,
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <PersonaAvatar name={p.name} size={36} showStatus status={level} queueCount={queueCount} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 14, fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        {p.name.charAt(0).toUpperCase() + p.name.slice(1)}
                        {level === 'idle' && (
                          <span style={{
                            fontSize: 9, padding: '1px 5px', borderRadius: 4,
                            background: 'rgba(210,153,34,0.15)', color: '#d29922', fontWeight: 600,
                          }}>IDLE</span>
                        )}
                      </div>
                      {p.status_line && (
                        <div style={{
                          fontSize: 11, color: 'var(--text-muted)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          marginTop: 2,
                        }}>
                          {p.status_line}
                        </div>
                      )}
                      {p.context_pct != null && (
                        <div style={{ marginTop: 4 }}>
                          <ContextBar pct={p.context_pct} />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Sleeping */}
        {sleeping.length > 0 && (
          <div>
            <div style={{
              fontSize: 11, textTransform: 'uppercase', letterSpacing: '1.2px',
              color: 'var(--text-muted)', marginBottom: 12, paddingLeft: 4,
            }}>
              Sleeping ({sleeping.length})
            </div>
            <div style={{
              display: 'grid', gap: 8,
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            }}>
              {sleeping.map(p => {
                const entry = statusMap[p.name.toLowerCase()]
                const queueCount = entry?.queueCount ?? 0
                const isSpawning = spawning[p.name]
                return (
                  <div key={p.name} style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 10, padding: '10px 14px',
                    display: 'flex', alignItems: 'center', gap: 10,
                    opacity: 0.7,
                  }}>
                    <PersonaAvatar name={p.name} size={28} showStatus status="sleeping" queueCount={queueCount} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 500,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        {p.name.charAt(0).toUpperCase() + p.name.slice(1)}
                        {queueCount > 0 && (
                          <span style={{
                            fontSize: 9, padding: '1px 5px', borderRadius: 4,
                            background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 600,
                          }}>{queueCount}Q</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleWake(p.name)}
                      disabled={isSpawning}
                      title={`Wake ${p.name}`}
                      style={{
                        width: 28, height: 28, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isSpawning ? 'var(--success)' : 'rgba(74,222,128,0.15)',
                        border: '1px solid rgba(74,222,128,0.3)',
                        cursor: isSpawning ? 'wait' : 'pointer',
                        opacity: isSpawning ? 0.5 : 1,
                        color: 'var(--success)', fontSize: 12,
                        transition: 'all 0.2s',
                      }}
                    >
                      {isSpawning ? '...' : '⏻'}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        </>)}
      </div>
    </div>
  )
}
